import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Send, Loader2 } from "lucide-react";
import Header from "@/components/Header";
import ProgressBar from "@/components/ProgressBar";
import StepAboutYou from "@/components/steps/StepAboutYou";
import StepExperience from "@/components/steps/StepExperience";
import StepAvailability from "@/components/steps/StepAvailability";
import StepRequirements from "@/components/steps/StepRequirements";
import StepReview from "@/components/steps/StepReview";
import SuccessScreen from "@/components/SuccessScreen";
import { initialData, validateStep, needsLogistics, displayVal, STEP_NAMES } from "@/lib/formConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DRAFT_KEY = "cliq-application-draft";

const HERO_IMG =
  "https://images.unsplash.com/photo-1746587224861-521244472226?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxwaG90byUyMGJvb3RoJTIwcGFydHklMjBldmVudCUyMGNvbmZldHRpJTIwY2VsZWJyYXRpb24lMjBndWVzdHN8ZW58MHx8fHwxNzg5ODU5MjQ5fDA&ixlib=rb-4.1.0&q=85";

export default function Application() {
  const [step, setStep] = useState(1);
  const [data, setData] = useState(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) return { ...initialData, ...JSON.parse(raw) };
    } catch { /* ignore corrupt draft */ }
    return initialData;
  });
  const [submitting, setSubmitting] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [imgOk, setImgOk] = useState(true);

  useEffect(() => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(data));
  }, [data]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const setField = (name, value) => setData((prev) => ({ ...prev, [name]: value }));
  const toggleMulti = (name, value) =>
    setData((prev) => {
      const arr = prev[name] || [];
      return { ...prev, [name]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] };
    });

  const stepValid = validateStep(step, data);
  const logistics = needsLogistics(data.position);

  const buildPayload = () => ({
    email: data.email.trim(),
    position: data.position,
    firstName: data.firstName.trim(),
    lastName: data.lastName.trim(),
    phone: data.phone.trim(),
    address: data.address.trim(),
    is18: data.is18,
    workAuth: data.workAuth,
    shirtSize: data.shirtSize,
    instagram: data.instagram.trim(),
    driversLicense: logistics ? data.driversLicense : "",
    yearsDriving: logistics ? data.yearsDriving.trim() : "",
    cargoVan: logistics ? String(displayVal(data, "cargoVan") || "") : "",
    lift50: logistics ? data.lift50 : "",
    loadTransport: logistics ? data.loadTransport : "",
    availableDays: data.availableDays,
    earlyLate: String(displayVal(data, "earlyLate") || ""),
    nycCommute: data.nycCommute,
    liCommute: data.liCommute,
    availabilityWindow: data.availabilityWindow.trim(),
    workedEvents: data.workedEvents,
    eventTypes: data.eventTypes,
    describeExperience: data.describeExperience.trim(),
    techComfort: data.techComfort,
    familiarTech: data.familiarTech,
    aboutYou: data.aboutYou.trim(),
    traits: data.traits,
    physicalAck: data.physicalAck,
    dressCode: data.dressCode,
    whyCliq: data.whyCliq.trim(),
    anythingElse: data.anythingElse.trim(),
    confirm: data.confirm,
  });

  const handleSubmit = async () => {
    if (!validateStep(5, data) || submitting) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/applications`, buildPayload());
      setApplicationId(res.data.applicationId);
      localStorage.removeItem(DRAFT_KEY);
      window.scrollTo({ top: 0 });
    } catch {
      toast.error("Something went wrong submitting your application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (applicationId) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Header />
        <SuccessScreen applicationId={applicationId} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-10">
      <Header />

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-6 sm:space-y-8">
        {step === 1 && (
          <div className="space-y-5">
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center rounded-full bg-slate-900 text-white text-xs font-bold px-3.5 py-1.5">
                $20–$35/hr
              </span>
              <span className="inline-flex items-center rounded-full border border-slate-300 bg-white text-slate-700 text-xs font-bold px-3.5 py-1.5">
                Part Time / Event-Based
              </span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              Join the CLiQ<span className="text-rose-600">!</span> Booths Team
            </h1>
            <p className="text-base sm:text-lg text-slate-900 leading-relaxed">
              Help us create memorable experiences at events throughout New York.
            </p>
            <p className="text-sm text-slate-900 leading-relaxed">
              We're looking for reliable, professional and outgoing people interested in joining our event team.
              Events are primarily in New York City and Long Island — you must have reliable transportation to event
              sites. CLiQ! Booths is an At-Will employer. By continuing, you acknowledge the requirements and pay
              range above.
            </p>
            {imgOk && (
              <img
                src={HERO_IMG}
                alt="Guests enjoying a CLiQ! Booths photo booth experience"
                onError={() => setImgOk(false)}
                className="w-full h-40 sm:h-52 object-cover rounded-2xl shadow-md"
              />
            )}
          </div>
        )}

        <ProgressBar step={step} />

        <section
          key={step}
          data-testid={`step-${step}-panel`}
          className="step-enter rounded-3xl border-2 border-slate-200 bg-white shadow-sm p-5 sm:p-8"
        >
          {step === 1 && <StepAboutYou data={data} setField={setField} />}
          {step === 2 && <StepExperience data={data} setField={setField} toggleMulti={toggleMulti} />}
          {step === 3 && <StepAvailability data={data} setField={setField} />}
          {step === 4 && <StepRequirements data={data} setField={setField} />}
          {step === 5 && <StepReview data={data} goToStep={setStep} setField={setField} />}
        </section>

        <div className="space-y-3">
          <div className="flex gap-3">
            {step > 1 && (
              <button
                type="button"
                data-testid={`step-${step}-back-button`}
                onClick={() => setStep((s) => s - 1)}
                className="h-[52px] px-5 sm:px-7 rounded-xl border-2 border-slate-300 bg-white font-heading font-bold text-slate-700 inline-flex items-center gap-2 transition-all hover:border-slate-900 active:scale-[0.98]"
              >
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            )}
            {step < 5 ? (
              <button
                type="button"
                data-testid={`step-${step}-next-button`}
                disabled={!stepValid}
                onClick={() => stepValid && setStep((s) => s + 1)}
                className="flex-1 h-[52px] rounded-xl bg-slate-900 text-white font-heading font-bold text-base inline-flex items-center justify-center gap-2 shadow-md transition-all hover:bg-slate-800 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-slate-900"
              >
                {step === 4 ? "Review Application" : "Continue"} <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                data-testid="step-5-submit-button"
                disabled={!stepValid || submitting}
                onClick={handleSubmit}
                className="flex-1 h-[52px] rounded-xl bg-rose-600 text-white font-heading font-bold text-base inline-flex items-center justify-center gap-2 shadow-md transition-all hover:bg-rose-700 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" /> Submitting…
                  </>
                ) : (
                  <>
                    Submit Application <Send className="h-4 w-4" />
                  </>
                )}
              </button>
            )}
          </div>
          {!stepValid && (
            <p aria-live="polite" className="text-sm text-slate-500 text-center">
              Complete the required fields on this page to continue — Step {step} of {STEP_NAMES.length}.
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
